/**
 * Adaptive encode FPS from encoded delta frame sizes (not pixel analysis).
 * Keyframes are ignored — they skew the budget.
 */
export class DesktopAdaptiveFps {
  /**
   * @param {{ minFps?: number, maxFps?: number, startFps?: number, targetBitrate?: number, windowSize?: number }} [options]
   */
  constructor({
    minFps = 8,
    maxFps = 15,
    startFps = 10,
    targetBitrate = 1_500_000,
    windowSize = 10
  } = {}) {
    this.minFps = minFps;
    this.maxFps = maxFps;
    this.targetFps = startFps;
    this.targetBitrate = targetBitrate;
    this.windowSize = windowSize;
    this.frames = [];
    this.lastChangeAt = 0;
  }

  /**
   * @param {EncodedVideoChunk} chunk
   */
  onEncodedChunk(chunk) {
    if (chunk.type === "key") {
      return;
    }
    this.frames.push({ bytes: chunk.byteLength, ts: performance.now() });
    if (this.frames.length > this.windowSize) {
      this.frames.shift();
    }
  }

  getStats() {
    const values = this.frames.map(frame => frame.bytes).sort((a, b) => a - b);
    if (values.length === 0) {
      return null;
    }
    const sum = values.reduce((total, value) => total + value, 0);
    return {
      avg: sum / values.length,
      median: values[Math.floor(values.length / 2)],
      p80: values[Math.floor(values.length * 0.8)],
      count: values.length
    };
  }

  /**
   * @param {{ encoderQueueSize?: number, sendQueueBytes?: number }} signals
   * @returns {number}
   */
  update(signals = {}) {
    if (this.frames.length < 5) {
      return this.targetFps;
    }

    const now = performance.now();
    if (now - this.lastChangeAt < 1200) {
      return this.targetFps;
    }

    const encoderBusy = (signals.encoderQueueSize ?? 0) > 2;
    const networkBusy = (signals.sendQueueBytes ?? 0) > 512_000;

    if (encoderBusy || networkBusy) {
      this.targetFps = Math.max(this.minFps, this.targetFps - 2);
      this.lastChangeAt = now;
      return this.targetFps;
    }

    const budgetBytesPerFrame = this.targetBitrate / 8 / this.targetFps;
    let small = 0;
    let big = 0;

    for (const frame of this.frames) {
      const ratio = frame.bytes / budgetBytesPerFrame;
      if (ratio < 0.25) {
        small += 1;
      } else if (ratio > 0.85) {
        big += 1;
      }
    }

    const stats = this.getStats();
    const count = this.frames.length;
    const manySmall = small >= count * 0.7;
    const manyBig = big >= count * 0.5;
    const p80Big = stats && stats.p80 > budgetBytesPerFrame * 1.2;

    if (manySmall) {
      this.targetFps = Math.max(this.minFps, this.targetFps - 1);
      this.lastChangeAt = now;
      return this.targetFps;
    }

    if (manyBig || p80Big) {
      this.targetFps = Math.min(this.maxFps, this.targetFps + 1);
      this.lastChangeAt = now;
      return this.targetFps;
    }

    return this.targetFps;
  }
}
